"use client";

import { IconChainLink4 } from "central-icons/IconChainLink4";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { AnimatePresence, m } from "motion/react";
import DemoWrapper from "@/components/preview/demo-wrapper";
import { AnimatedIcon } from "@/components/ui/animated-icon";
import { Button } from "@/components/ui/button";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { useSpeech } from "@/hooks/use-speech";

const ANNOUNCEMENT = "Link copied";

const pillTransition = { bounce: 0, duration: 0.4, type: "spring" as const };

function AnnouncementPill({
  children,
  label,
  show,
}: {
  children: React.ReactNode;
  label: string;
  show: boolean;
}) {
  return (
    <div className="relative flex items-center justify-center">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-full z-0 mb-2 flex justify-center"
      >
        <AnimatePresence>
          {show && (
            <m.span
              animate={{ filter: "blur(0px)", opacity: 1, y: 0 }}
              className="w-max select-none rounded-full bg-gray-1200 px-3 py-1.5 font-medium text-gray-100 text-sm will-change-transform"
              exit={{ filter: "blur(4px)", opacity: 0, y: 0 }}
              initial={{ filter: "blur(4px)", opacity: 0, y: 24 }}
              transition={pillTransition}
            >
              {label}
            </m.span>
          )}
        </AnimatePresence>
      </div>
      {children}
    </div>
  );
}

export default function AccessibilityLiveRegionsDemo() {
  const { isCopied: copied, copy } = useCopyToClipboard();
  const { speak } = useSpeech();

  const handleCopy = async () => {
    if (await copy(window.location.href)) {
      speak(ANNOUNCEMENT);
    }
  };

  return (
    <DemoWrapper margin="">
      <div className="flex h-40 w-full items-center justify-center">
        <AnnouncementPill label={ANNOUNCEMENT} show={copied}>
          <Button
            className="relative z-10"
            leftIcon={
              <AnimatedIcon
                active={copied}
                activeIcon={
                  <IconCircleCheck className="size-4.5 text-gray-1000 will-change-transform" />
                }
                idleIcon={
                  <IconChainLink4 className="size-4.5 text-gray-1000 will-change-transform" />
                }
              />
            }
            onClick={handleCopy}
            rounded
            size="md"
            type="button"
            variant="secondary"
          >
            Copy link
          </Button>
        </AnnouncementPill>

        <span className="sr-only" role="status">
          {copied ? ANNOUNCEMENT : ""}
        </span>
      </div>
    </DemoWrapper>
  );
}
